import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchMCPServers } from '../services/mcpApi';

const MCPContext = createContext();

export const MCPProvider = ({ children }) => {
  const [mcpServers, setMcpServers] = useState([]);
  const [selectedMCPs, setSelectedMCPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load MCP servers on mount
  useEffect(() => {
    loadMCPServers();
  }, []);

  // Load selected MCPs from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('selectedMCPs');
    if (saved) {
      try {
        setSelectedMCPs(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved MCPs:', e);
      }
    }
  }, []);

  // Save selected MCPs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('selectedMCPs', JSON.stringify(selectedMCPs));
  }, [selectedMCPs]);

  const loadMCPServers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const servers = await fetchMCPServers();
      setMcpServers(servers);
    } catch (err) {
      console.error('Failed to load MCP servers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleMCP = useCallback((mcpId) => {
    setSelectedMCPs(prev => {
      if (prev.includes(mcpId)) {
        return prev.filter(id => id !== mcpId);
      } else {
        return [...prev, mcpId];
      }
    });
  }, []);

  const selectMCP = useCallback((mcpId) => {
    setSelectedMCPs(prev => {
      if (!prev.includes(mcpId)) {
        return [...prev, mcpId];
      }
      return prev;
    });
  }, []);

  const deselectMCP = useCallback((mcpId) => {
    setSelectedMCPs(prev => prev.filter(id => id !== mcpId));
  }, []);

  const clearSelectedMCPs = useCallback(() => {
    setSelectedMCPs([]);
  }, []);

  const setMCPs = useCallback((mcpIds) => {
    setSelectedMCPs(mcpIds);
  }, []);

  const getSelectedMCPObjects = useCallback(() => {
    return mcpServers.filter(server => selectedMCPs.includes(server.id));
  }, [mcpServers, selectedMCPs]);

  const value = {
    mcpServers,
    selectedMCPs,
    loading,
    error,
    loadMCPServers,
    toggleMCP,
    selectMCP,
    deselectMCP,
    clearSelectedMCPs,
    setMCPs,
    getSelectedMCPObjects
  };

  return <MCPContext.Provider value={value}>{children}</MCPContext.Provider>;
};

export const useMCP = () => {
  const context = useContext(MCPContext);
  if (!context) {
    throw new Error('useMCP must be used within an MCPProvider');
  }
  return context;
};
